import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]') || this.#buildUltraRichContent(html);
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (!productGrid) return;

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };


  /**
   * Adapt the custom Ultra Rich section without changing the normal product-grid path.
   * Its page-load script is deliberately not executed inside the popup.
   * @param {Document} html
   * @returns {Element | null}
   */
  #buildUltraRichContent(html) {
    const source = html.querySelector('.ur-product-hero');
    const stylesheet = html.querySelector('style[data-ur-product-styles]');
    if (!source?.querySelector('[data-ur-quick-add-config]') || !stylesheet) return null;

    const content = document.createElement('div');
    content.setAttribute('data-ur-quick-add', '');
    const hero = /** @type {HTMLElement} */ (source.cloneNode(true));
    hero.querySelectorAll('.ur-product-hero__breadcrumbs, .ur-product-hero__description').forEach((node) => node.remove());

    // A recommendation popup can share section IDs with the full product page.
    const ids = new Map();
    hero.querySelectorAll('[id]').forEach((node) => {
      const oldId = node.getAttribute('id') || '';
      const newId = oldId + '-quick-add';
      node.setAttribute('id', newId);
      ids.set(oldId, newId);
    });
    hero.querySelectorAll('[for], [aria-labelledby], [aria-describedby]').forEach((node) => {
      for (const attribute of ['for', 'aria-labelledby', 'aria-describedby']) {
        const value = node.getAttribute(attribute);
        if (value) node.setAttribute(attribute, value.split(' ').map((id) => ids.get(id) || id).join(' '));
      }
    });
    const error = document.createElement('p');
    error.className = 'ur-product-hero__form-error hidden';
    error.setAttribute('ref', 'addToCartTextError');
    hero.querySelector('product-form-component')?.appendChild(error);
    content.append(stylesheet.cloneNode(true), hero);
    return content;
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;


    const dialog = document.getElementById('quick-add-dialog');
    if (dialog instanceof QuickAddDialog) dialog.resetUltraRichProduct();
    const isUltraRich = productGrid.hasAttribute('data-ur-quick-add');
    modalContent.classList.toggle('ur-quick-add-content', isUltraRich);
    dialog?.querySelector('dialog')?.classList.toggle('ur-quick-add-modal', isUltraRich);
    if (isUltraRich && dialog instanceof QuickAddDialog) {
      const config = JSON.parse(productGrid.querySelector('[data-ur-quick-add-config]')?.textContent || '{}');
      if (!Array.isArray(config.variants)) return;
      // Fresh controls ensure native form lifecycle hooks and popup listeners initialize once.
      modalContent.replaceChildren(...productGrid.childNodes);
      dialog.initializeUltraRichProduct(config);
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  /** @type {AbortController | null} */
  #ultraRichController = null;

  resetUltraRichProduct() {
    this.#ultraRichController?.abort();
    this.#ultraRichController = null;
  }

  /**
   * Bind only the custom controls cloned into this dialog. Cart submission remains native.
   * @param {{addText: string, soldOutText: string, unavailableText: string, addingText: string, variants: Array<{id: number, available: boolean, options: string[], price: string, min: number, step: number, max: number | null, image: {src: string, alt: string} | null}>}} config
   */
  initializeUltraRichProduct(config) {
    this.resetUltraRichProduct();
    this.#ultraRichController = new AbortController();
    const { signal } = this.#ultraRichController;
    const root = this.querySelector('.ur-product-hero');
    const form = root?.querySelector('form');
    const variantInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'));
    const quantity = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="quantity"]'));
    const button = /** @type {HTMLButtonElement | null} */ (form?.querySelector('button[name="add"]'));
    const label = button?.querySelector('[data-add-to-cart-text]');
    const price = root?.querySelector('[data-product-price]');
    const image = /** @type {HTMLImageElement | null} */ (root?.querySelector('.ur-product-hero__image'));
    if (!root || !form || !variantInput || !quantity || !button || !label) return;
    let pending = false;

    const selectedVariant = () => {
      const groups = Array.from(form.querySelectorAll('.ur-product-hero__option'));
      const options = groups.map((group) => /** @type {HTMLInputElement | null} */ (group.querySelector('input:checked'))?.value);
      return groups.length
        ? config.variants.find((variant) => variant.options.every((value, index) => value === options[index]))
        : config.variants[0];
    };
    const normalizeQuantity = () => {
      const min = Number(quantity.min) || 1;
      const step = Number(quantity.step) || 1;
      const max = quantity.max ? Number(quantity.max) : Infinity;
      const value = Math.min(max, Math.max(min, Number(quantity.value) || min));
      quantity.value = String(min + Math.floor((value - min) / step) * step);
    };
    const updateVariant = () => {
      const variant = selectedVariant();
      variantInput.value = variant ? String(variant.id) : '';
      button.disabled = pending || !variant?.available;
      label.textContent = pending ? config.addingText : !variant ? config.unavailableText : variant.available ? config.addText : config.soldOutText;
      if (!variant) return;
      if (price) price.textContent = variant.price;
      quantity.min = String(variant.min || 1);
      quantity.step = String(variant.step || 1);
      if (variant.max != null) quantity.max = String(variant.max);
      else quantity.removeAttribute('max');
      normalizeQuantity();
      if (image && variant.image?.src) {
        image.src = variant.image.src;
        image.alt = variant.image.alt;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        form.querySelector('add-to-cart-component')?.setAttribute('data-product-variant-media', variant.image.src);
      }
    };
    form.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.matches('.ur-product-hero__variant-radio')) updateVariant();
      if (event.target === quantity) normalizeQuantity();
    }, { signal });
    form.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target.closest('[data-qty-minus], [data-qty-plus]') : null;
      if (!target) return;
      event.preventDefault();
      normalizeQuantity();
      quantity.stepUp(target.hasAttribute('data-qty-minus') ? -1 : 1);
      normalizeQuantity();
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }, { signal });
    root.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      const cartEvent = /** @type {CartLinesUpdateEvent} */ (event);
      if (!cartEvent.promise) return;
      pending = true;
      updateVariant();
      cartEvent.promise.catch(() => {}).finally(() => {
        pending = false;
        if (root.isConnected) updateVariant();
      });
    }, { signal });
    updateVariant();
  }

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.resetUltraRichProduct();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    this.resetUltraRichProduct();
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
